import React, {
    FC,
    useContext,
    useState,
    MouseEvent,
    ReactNode,
    useMemo,
    useCallback,
    JSX,
} from 'react';

import TextInput from '../../../../../components/inputs/textInput';
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

import { EditorContext } from '../../../../../context/EditorContext';

import useOutsideClickDetector from '../../../../../hooks/useOutsideClickDetector';

import { DatasetModel } from '../../../../../models/dataset.model';
import { ImageVariantElement } from '../../../../../models/editor.model';
import { VariantWithLabel } from 'models/variant.model';

import WarningIconComponent from '../../WarningIconComponent';
import DatasetElementView from '../DatasetElementView';
import DatasetIconDropDown from '../DatasetIconDropDown';

interface ImageInputProps {
    isDisabled?: boolean;
    handleClickDatasetIcon: (e: MouseEvent<HTMLSpanElement>) => void;
    datasetData?: DatasetModel;
    element: ImageVariantElement;
    updateStructureForDataset: () => void;
    hasWarning: boolean;
    isEmail: boolean;
    handleAdd: () => void;
    text?: string;
    setText: (val: string) => void;
    setShowImageAlternateIconModal: (val: boolean) => void;
    handleDatasetFieldClick: (val: number) => void;
    data?: VariantWithLabel[];
}

const ImageInput: FC<ImageInputProps> = ({
    isDisabled,
    handleClickDatasetIcon,
    datasetData,
    element,
    updateStructureForDataset,
    hasWarning,
    isEmail,
    handleAdd,
    setText,
    text,
    setShowImageAlternateIconModal,
    handleDatasetFieldClick,
    data,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const inputRef = useOutsideClickDetector(() => setIsFocused(false));

    const datasetFieldId = useMemo<number | undefined>(() => {
        if (element && element['datasetField'] !== undefined)
            return Number(element['datasetField']);
    }, [element]);

    const hasDatasetField = useMemo<boolean>(
        () => datasetFieldId !== undefined,
        [datasetFieldId],
    );

    const ToolTipTitle = useMemo<ReactNode | undefined>(() => {
        if (datasetData && datasetFieldId !== undefined) {
            const field = datasetData.fields[datasetFieldId];
            return (
                <ToolTipText>
                    <Text>Dataset name: {datasetData.name}</Text>
                    <Text>Field name: {field.name}</Text>
                    <Text>Sample: {field.sample}</Text>
                </ToolTipText>
            );
        }
        return undefined;
    }, [datasetData, datasetFieldId]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            if (event.key === 'Backspace' && datasetFieldId !== null) {
                updateStructureForDataset();
            }
        },
        [datasetFieldId, updateStructureForDataset],
    );

    const showDatasetIcon = useMemo<boolean>(
        () => !!(formula?.dataset_id && isFocused),
        [formula, isFocused],
    );

    const DatasetIcon = useMemo<JSX.Element | null>(() => {
        if (showDatasetIcon) {
            return (
                <DatasetIconDropDown
                    showIcon
                    handleDatasetFieldClick={(id) =>
                        handleDatasetFieldClick(id)
                    }
                />
            );
        }
        return null;
    }, [handleDatasetFieldClick, showDatasetIcon]);

    const masterVariant = useMemo<VariantWithLabel | undefined>(
        () => data?.find((item) => item?.id === item?.component_id),
        [data],
    );

    const masterVariantText = useMemo<string>(() => {
        return masterVariant?.content ?? '';
    }, [masterVariant]);
    return (
        <Container ref={inputRef}>
            {hasDatasetField && formula?.dataset_id ? (
                <StyledDatasetElementContainer isFocused={isFocused}>
                    <StyledDatasetElementView
                        handleClick={(e) =>
                            isDisabled ? {} : handleClickDatasetIcon(e)
                        }
                        aampeToolTipTitle={ToolTipTitle!}
                        datasetFieldSample={
                            (datasetData?.fields[datasetFieldId!]?.sample ??
                                '') as string
                        }
                    />
                    <InvisibleInput onKeyDown={handleKeyDown} />
                </StyledDatasetElementContainer>
            ) : (
                <StyledTextField
                    hasWarning={hasWarning}
                    placeholder="Add image URL"
                    value={text}
                    wrapperStyle={{
                        padding: aampeTheme.spacing(0),
                    }}
                    isDisabled={isDisabled || !!masterVariantText}
                    trailingIcon={
                        isEmail && masterVariantText ? (
                            hasWarning ? (
                                <IconContainer>
                                    <WarningIconComponent
                                        type={element?.variantType}
                                    />
                                    <ICONS.imageAlternate />
                                </IconContainer>
                            ) : (
                                <ICONS.imageAlternate />
                            )
                        ) : formula?.dataset_id ? (
                            DatasetIcon
                        ) : null
                    }
                    trailingIconAction={() => {
                        if (isEmail && masterVariant)
                            setShowImageAlternateIconModal(true);
                    }}
                    onChange={(e) => {
                        setText(e.target.value);
                        handleAdd();
                    }}
                    onClick={() => setIsFocused(true)}
                />
            )}
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    rowGap: aampeTheme.spacing4(2),
});

const StyledDatasetElementContainer = styled.div<{ isFocused: boolean }>(
    (props) => ({
        borderRadius: aampeTheme.borderRadius.default,
        border: `1px solid  ${
            props.isFocused
                ? aampeTheme.colors.solidA02
                : aampeTheme.colors.bordersWG01
        }`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        background: aampeTheme.colors.white,
        padding: aampeTheme.spacing4(1),
        position: 'relative',
    }),
);

const InvisibleInput = styled.input(() => ({
    color: aampeTheme.colors.neutral09,
    justifySelf: 'center',
    outline: 'none',
    fontSize: aampeTheme.size.font.default,
    lineHeight: aampeTheme.size.lineHeight.textSmall,
    fontWeight: aampeTheme.weights.light,
    padding: aampeTheme.spacing4(2),
    wordBreak: 'break-word',
    minHeight: '37px',
    border: 'none',
    '&::placeholder': {
        color: aampeTheme.colors.textAccessibleWG01,
        opacity: 0.8,
    },
}));

const StyledDatasetElementView = styled(DatasetElementView)({
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.interactiveWG01,
    padding: aampeTheme.spacing4(0, 1),
    color: aampeTheme.colors.neutral09,
    lineHeight: aampeTheme.size.lineHeight.default,
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.light,
    minWidth: '30px',
    border: `1px solid ${aampeTheme.colors.solidA02}`,
});

const StyledTextField = styled(TextInput)<{ hasWarning: boolean }>(
    ({ hasWarning }) => ({
        width: '100%',
        padding: aampeTheme.spacing4(0),
        '& .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${
                hasWarning
                    ? aampeTheme.colors.error
                    : aampeTheme.colors.bordersWG01
            }`,
        },
        '& .MuiInputBase-root': {
            backgroundColor: aampeTheme.colors.white,
            borderRadius: aampeTheme.borderRadius.default,
            borderColor: aampeTheme.colors.white,
            minHeight: '45px',
            '&:hover fieldset': {
                borderColor: aampeTheme.colors.bordersWG01,
            },
            '&.Mui-focused fieldset': {
                border: `1px solid ${aampeTheme.colors.solidA02}`,
            },
        },
    }),
);

const IconContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});

const ToolTipText = styled.div({
    display: 'flex',
    flexDirection: 'column',
    padding: aampeTheme.spacing4(2),
});

const Text = styled.div({
    fontSize: aampeTheme.size.font.default,
    lineHeight: aampeTheme.size.lineHeight.default,
    fontWeight: aampeTheme.weights.light,
});

export default ImageInput;
